import { FC, useEffect, useState } from 'react';
import { WrapInLayout } from '../layouts';
import SignInForm from '../components/oauth/SignInForm';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useTypedSelector } from '../hooks/useTypedSelector';
import { getCurrentUser } from '../store/authSlice';
import useSchemaSearchParams from '../hooks/useSerarchParams';
import { useAccess } from '../hooks/useAccess';

const initState = {
  login: '',
  password: '',
  token: '',
};
export type ILoginState = typeof initState;

const SignInPage: FC<{ originalUrl?: string }> = ({ originalUrl = '/' }) => {
  const user = useTypedSelector(getCurrentUser());
  const [loginState, setLoginState] = useState<ILoginState>(() => initState);
  const [loginErrState, setLoginErrState] = useState<Partial<Record<keyof ILoginState, string>>>();
  const [loading, setLoading] = useState<boolean>(false);
  const searchParams = useSchemaSearchParams();

  const navigate = useNavigate();
  const { handleLogin } = useAuth();
  const { getPathAccess, getLandingPage } = useAccess();

  const landing = getLandingPage();

  // --
  // -- Once signed in, go on to the page they came for.
  // --
  // -- Only if they can actually open it. A page they cannot open redirects
  // -- back here, and this effect sends them straight back to it: the two
  // -- bounce forever on a blank screen, which is what a vendor hit when the
  // -- access map had no ROLE_VENDOR in it.
  // --
  // -- With nowhere to send them the effect does nothing, and the render below
  // -- says so rather than spinning.
  // --
  useEffect(() => {
    if (!user) return;

    const target = searchParams.get('original-url') || originalUrl;
    if (getPathAccess(target)) { navigate(target); return; }
    if (landing) navigate(landing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogIn = async () => {
    setLoading(() => true);
    setLoginErrState(() => undefined);

    const errs = await handleLogin(loginState);

    errs
      ? setLoginErrState(() => errs)
      : navigate(originalUrl);

    setLoading(() => false);
  }

  // -- Signed in, but this account opens nothing. Saying so beats a blank
  // -- screen, and beats a redirect that comes straight back.
  if (user && !landing) {
    return (
      <WrapInLayout title="No access" layout="center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold mb-2">No access</h1>
          <p className="text-text_pale">
            This account is signed in but has no pages available to it. Ask an
            administrator to check the roles on your account.
          </p>
        </div>
      </WrapInLayout>
    );
  }

  return user === null
    ? (
      <WrapInLayout title="Sign In" layout="center">
        <SignInForm
          state={loginState}
          stateErr={loginErrState}
          setState={setLoginState}
          loading={loading}
          onSignIn={handleLogIn}
        />
      </WrapInLayout>
    ) : null;
};

export default SignInPage;
